import { isCandidateVisible } from "../../models/market-segment"
import type { UVInsertConfig } from "../../models/uv-inserts"
import type { SuggestedMessaging } from "../../models/uv-inserts/suggested-messaging"

/**
 * Segment resolution for UV insert configs (gift-wrap flow only).
 *
 * A config declares the market segment(s) it is eligible for via `marketSegment`
 * (empty → WOMENS, per the shared market-segment rule). The product being wrapped
 * provides the context segment.
 *
 * Rather than dropping ineligible configs (absence makes their option visible in the
 * option tree), this marks them `active: false`, so the existing option-visibility /
 * single-path / bubble-up navigation treats them as unavailable and collapses the tree
 * — hiding individual options when only some segments match, and (combined with the
 * step-level skip below) never entering the inserts pages when none match.
 *
 * Note: this is gift-wrap-specific. The personalisation flow consumes configs directly
 * and is intentionally not segment-filtered — it can target a UV insert regardless of
 * market segment.
 */
export function applyUvInsertSegmentEligibility(
  configs: UVInsertConfig[],
  contextSegment: string | null | undefined,
): UVInsertConfig[] {
  return configs.map((config) =>
    isCandidateVisible(config.marketSegment, contextSegment)
      ? config
      : { ...config, active: false },
  )
}

/**
 * True when at least one config is both active and eligible for the context segment.
 * When this is false, the gift-wrap flow should skip the UV inserts step entirely.
 */
export function hasActiveUvInsertConfigForSegment(
  configs: UVInsertConfig[],
  contextSegment: string | null | undefined,
): boolean {
  return configs.some(
    (config) => config.active && isCandidateVisible(config.marketSegment, contextSegment),
  )
}

/**
 * Drops suggested-messaging groups not eligible for the context segment.
 * Unlike configs, groups can be removed outright — nothing in the option tree
 * keys off their presence. Empty `marketSegment` → WOMENS, per the shared rule.
 */
export function filterUvSuggestedMessagingForSegment(
  suggestedMessaging: SuggestedMessaging,
  contextSegment: string | null | undefined,
): SuggestedMessaging {
  return suggestedMessaging.filter((group) =>
    isCandidateVisible(group.marketSegment, contextSegment),
  )
}
